import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import ErrorBoundary from './components/ErrorBoundary';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import Layout from './components/Layout';
import Login from './pages/Login';

// Lazy-loaded pages — only downloaded when the user navigates to them
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const ChangePassword = React.lazy(() => import('./pages/ChangePassword'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const SiteMonitor = React.lazy(() => import('./pages/SiteMonitor'));
const SiteSchedule = React.lazy(() => import('./pages/SiteSchedule'));
const SiteSettings = React.lazy(() => import('./pages/SiteSettings'));
const SiteEnergy = React.lazy(() => import('./pages/SiteEnergy'));
const SiteLearning = React.lazy(() => import('./pages/SiteLearning'));
const SiteManualOverride = React.lazy(() => import('./pages/SiteManualOverride'));
const SiteCommissioning = React.lazy(() => import('./pages/SiteCommissioning'));
const SiteCameraCommissioning = React.lazy(() => import('./pages/SiteCameraCommissioning'));
const SiteAlerts = React.lazy(() => import('./pages/SiteAlerts'));
const SiteIntelligence = React.lazy(() => import('./pages/SiteIntelligence'));
const SiteCamera = React.lazy(() => import('./pages/SiteCamera'));
const SiteIoLive = React.lazy(() => import('./pages/SiteIoLive'));
const AdminUsers = React.lazy(() => import('./pages/AdminUsers'));
const AdminDevices = React.lazy(() => import('./pages/AdminDevices'));
const SimInventory = React.lazy(() => import('./pages/SimInventory'));
const OrgSettings = React.lazy(() => import('./pages/OrgSettings'));
const Customers = React.lazy(() => import('./pages/Customers'));
const CustomerDetail = React.lazy(() => import('./pages/CustomerDetail'));
const SetupGuide = React.lazy(() => import('./pages/SetupGuide'));
const Manual = React.lazy(() => import('./pages/Manual'));
const OnboardingGuide = React.lazy(() => import('./pages/OnboardingGuide'));
const Engineers = React.lazy(() => import('./pages/Engineers'));
const Invoices = React.lazy(() => import('./pages/Invoices'));
const InvoiceSettings = React.lazy(() => import('./pages/InvoiceSettings'));
const SiteCircuits = React.lazy(() => import('./pages/SiteCircuits'));
const SiteSetupWizard = React.lazy(() => import('./pages/SiteSetupWizard'));
const SiteSelector = React.lazy(() => import('./pages/SiteSelector'));
const AuditTrail = React.lazy(() => import('./pages/AuditTrail'));
const HardwareCalculator = React.lazy(() => import('./pages/HardwareCalculator'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const SkipCancel = React.lazy(() => import('./pages/SkipCancel'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Terms = React.lazy(() => import('./pages/Terms'));

const PageLoader = () => <div style={{ padding: 40, color: '#5a6581' }}>Loading...</div>;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Platform owner-only pages (Invoices, Engineers, Device Fleet, Staff Accounts) */
function PlatformRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_platform && user.role === 'OWNER') return <>{children}</>;
  return <Navigate to="/select-site" replace />;
}

/** Platform staff pages — owners AND engineers (Dashboard, Customers) */
function PlatformStaffRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.is_platform && (user.role === 'OWNER' || user.role === 'ENGINEER')) return <>{children}</>;
  return <Navigate to="/select-site" replace />;
}

/** Owner-only pages (Org Settings — genuinely owner-only) */
function OwnerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'OWNER') return <>{children}</>;
  return <Navigate to="/select-site" replace />;
}

/**
 * Site-configuration pages — Setup Wizard, Site Settings, Circuits.
 *
 * These pages capture on-site physical facts (valve type, pump type,
 * sensor wiring, module count) that engineers know from being at the
 * plant, not the customer OWNER stuck at HQ. The backend has always
 * accepted `require_role("OWNER", "ENGINEER")` for these endpoints
 * and even has an auto-assign-site-to-engineer path in the wizard's
 * save handler (`services/routes/setup_wizard.py` section 8). But
 * the frontend was locked to OWNER-only by commit f992fb8 in April
 * 2026 during a legitimate engineer-lockdown pass — the wizard got
 * bundled with genuine owner-config pages by mistake, no contract
 * test caught the mismatch, and platform engineers have been unable
 * to run the wizard through the UI ever since.
 *
 * Access model matches the backend intent:
 *   - Customer OWNER can access their own site's config (unchanged).
 *   - SmartPitch HQ ENGINEER (role=ENGINEER + is_platform=true) can
 *     access sites they're assigned to. Backend enforces
 *     EngineerSiteAccess separately on write endpoints.
 *   - Non-platform ENGINEER (a customer's own engineer user) is
 *     rejected — that's a separate customer-user model.
 */
function OwnerOrPlatformEngineerRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'OWNER') return <>{children}</>;
  if (user.role === 'ENGINEER' && user.is_platform) return <>{children}</>;
  return <Navigate to="/select-site" replace />;
}

/** Bare /sites/:siteId redirects to the site monitor. */
function SiteIdRedirect() {
  const { siteId } = useParams<{ siteId: string }>();
  return <Navigate to={`/sites/${siteId}/monitor`} replace />;
}

/** Role-aware index redirect */
function IndexRedirect() {
  const { user, loading } = useAuth();

  if (loading) return <div style={{ padding: 40 }}>Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;

  // Platform owners and engineers go to dashboard
  if (user.is_platform && (user.role === 'OWNER' || user.role === 'ENGINEER')) {
    return <Navigate to="/dashboard" replace />;
  }

  // Customers go to site selector
  return <Navigate to="/select-site" replace />;
}

// One-time cleanup (2026-07-25): sp_lat / sp_lon were written by the removed
// Site Location tab and are no longer set or disclosed in the privacy policy.
// Remove any stale copies from existing users' browsers so the policy stays
// literally true. Safe to delete this block after a few releases.
try {
  localStorage.removeItem('sp_lat');
  localStorage.removeItem('sp_lon');
} catch {
  /* storage unavailable — nothing to clean */
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              {/* Phase 19m-C: per-route boundaries. Full-screen routes are
            individually wrapped so a crash inside (e.g.) Onboarding
            doesn't blank the SPA — the user can navigate to /login
            or any other page. Layout-wrapped routes get their own
            boundary inside Layout's <Outlet /> for the same reason. */}
              <Routes>
                <Route
                  path="/login"
                  element={
                    <RouteErrorBoundary>
                      <Login />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <RouteErrorBoundary>
                      <ForgotPassword />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <RouteErrorBoundary>
                      <ResetPassword />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/change-password"
                  element={
                    <RouteErrorBoundary>
                      <ChangePassword />
                    </RouteErrorBoundary>
                  }
                />
                {/* Public — reachable from the heads-up email link with no JWT. */}
                <Route
                  path="/skip-cancel/:token"
                  element={
                    <RouteErrorBoundary>
                      <SkipCancel />
                    </RouteErrorBoundary>
                  }
                />
                {/* Public legal pages — must be reachable pre-login so the
              login screen can link to them (GDPR/PECR requirement). */}
                <Route
                  path="/privacy"
                  element={
                    <RouteErrorBoundary>
                      <Privacy />
                    </RouteErrorBoundary>
                  }
                />
                <Route
                  path="/terms"
                  element={
                    <RouteErrorBoundary>
                      <Terms />
                    </RouteErrorBoundary>
                  }
                />

                {/* Full-screen pages — no Layout wrapper */}
                <Route
                  path="/select-site"
                  element={
                    <ProtectedRoute>
                      <RouteErrorBoundary>
                        <SiteSelector />
                      </RouteErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/onboarding"
                  element={
                    <ProtectedRoute>
                      <RouteErrorBoundary>
                        <Onboarding />
                      </RouteErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/sites/:siteId/monitor"
                  element={
                    <ProtectedRoute>
                      <RouteErrorBoundary>
                        <SiteMonitor />
                      </RouteErrorBoundary>
                    </ProtectedRoute>
                  }
                />
                {/* Layout-wrapped pages */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<IndexRedirect />} />

                  {/* Platform-only pages — SmartPitch HQ staff */}
                  <Route
                    path="dashboard"
                    element={
                      <PlatformStaffRoute>
                        <Dashboard />
                      </PlatformStaffRoute>
                    }
                  />
                  <Route
                    path="sites"
                    element={
                      <PlatformStaffRoute>
                        <Dashboard />
                      </PlatformStaffRoute>
                    }
                  />
                  <Route
                    path="customers"
                    element={
                      <PlatformStaffRoute>
                        <Customers />
                      </PlatformStaffRoute>
                    }
                  />
                  <Route
                    path="customers/:orgId"
                    element={
                      <PlatformStaffRoute>
                        <CustomerDetail />
                      </PlatformStaffRoute>
                    }
                  />
                  <Route
                    path="admin/users"
                    element={
                      <PlatformRoute>
                        <AdminUsers />
                      </PlatformRoute>
                    }
                  />
                  <Route
                    path="admin/engineers"
                    element={
                      <PlatformRoute>
                        <Engineers />
                      </PlatformRoute>
                    }
                  />
                  <Route
                    path="admin/devices"
                    element={
                      <PlatformRoute>
                        <AdminDevices />
                      </PlatformRoute>
                    }
                  />
                  <Route
                    path="admin/audit"
                    element={
                      <PlatformRoute>
                        <AuditTrail />
                      </PlatformRoute>
                    }
                  />
                  <Route
                    path="admin/sims"
                    element={
                      <PlatformRoute>
                        <SimInventory />
                      </PlatformRoute>
                    }
                  />

                  {/* Tools — platform staff (owners + engineers) */}
                  <Route
                    path="tools/hardware-calculator"
                    element={
                      <PlatformStaffRoute>
                        <HardwareCalculator />
                      </PlatformStaffRoute>
                    }
                  />
                  <Route
                    path="invoices"
                    element={
                      <PlatformRoute>
                        <Invoices />
                      </PlatformRoute>
                    }
                  />
                  <Route
                    path="invoices/settings"
                    element={
                      <PlatformRoute>
                        <InvoiceSettings />
                      </PlatformRoute>
                    }
                  />

                  {/* Owner-level pages */}
                  <Route
                    path="org/settings"
                    element={
                      <OwnerRoute>
                        <OrgSettings />
                      </OwnerRoute>
                    }
                  />

                  {/* Site selector inside Layout (engineers use this with sidebar) */}
                  <Route path="select-site" element={<SiteSelector />} />

                  {/* Guides — accessible to platform + owners + engineers */}
                  <Route path="setup-guide" element={<SetupGuide />} />
                  <Route path="manual" element={<Manual />} />
                  {/* Onboarding playbook — platform owners only (backend enforces too) */}
                  <Route
                    path="onboarding-guide"
                    element={
                      <PlatformRoute>
                        <OnboardingGuide />
                      </PlatformRoute>
                    }
                  />
                  {/* Legacy redirects */}
                  <Route path="documents" element={<Navigate to="/manual" replace />} />
                  <Route path="provisioning" element={<Navigate to="/setup-guide" replace />} />

                  {/* Bare /sites/:siteId redirects to the site monitor */}
                  <Route path="sites/:siteId" element={<SiteIdRedirect />} />

                  {/* Site pages — owner/platform only */}
                  <Route
                    path="sites/:siteId/settings"
                    element={
                      <OwnerOrPlatformEngineerRoute>
                        <SiteSettings />
                      </OwnerOrPlatformEngineerRoute>
                    }
                  />
                  <Route
                    path="sites/:siteId/circuits"
                    element={
                      <OwnerOrPlatformEngineerRoute>
                        <SiteCircuits />
                      </OwnerOrPlatformEngineerRoute>
                    }
                  />
                  <Route
                    path="sites/:siteId/setup"
                    element={
                      <OwnerOrPlatformEngineerRoute>
                        <SiteSetupWizard />
                      </OwnerOrPlatformEngineerRoute>
                    }
                  />

                  {/* Site pages — accessible to all roles */}
                  <Route path="sites/:siteId/schedule" element={<SiteSchedule />} />
                  <Route path="sites/:siteId/energy" element={<SiteEnergy />} />
                  <Route path="sites/:siteId/learning" element={<SiteLearning />} />
                  <Route path="sites/:siteId/manual" element={<SiteManualOverride />} />
                  <Route path="sites/:siteId/commissioning" element={<SiteCommissioning />} />
                  <Route path="sites/:siteId/camera-commissioning" element={<SiteCameraCommissioning />} />
                  <Route path="sites/:siteId/alerts" element={<SiteAlerts />} />
                  <Route path="sites/:siteId/intelligence" element={<SiteIntelligence />} />
                  <Route path="sites/:siteId/cameras" element={<SiteCamera />} />
                  <Route path="sites/:siteId/io" element={<SiteIoLive />} />
                </Route>

                {/* Catch-all: redirect unknown paths to index */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
